import React from 'react';
import { useQuery } from '@apollo/client';
import { gql } from '../graphql/generated/gql';
import NestableLink from '../utils/NestableLink';
import groupPath from '../utils/url/groupPath';
import useIsPublicView from '../utils/useIsPublicView';
import GroupDetailContent from './GroupDetailContent';
import * as styles from './GroupDetailPreview.module.scss';

const GROUP_DETAIL_PREVIEW_QUERY = gql(/** @complexity 6 */ `
  query GroupDetailPreview($id: ID!) {
    group(id: $id) {
      id
      ...GroupDetailContent_group
      ...groupPath_group
    }
  }
`);

const PUBLIC_GROUP_DETAIL_PREVIEW_QUERY = gql(/** @complexity 2 */ `
  query PublicGroupDetailPreview($id: ID!) {
    group: publicGroup(id: $id) {
      id
      ...GroupDetailContent_publicGroup
      ...groupPath_group
    }
  }
`);

interface GroupDetailPreviewProps {
  groupId: string;
  setMinHeight: (height: number) => void;
}

const GroupDetailPreview = ({
  groupId,
  setMinHeight,
}: GroupDetailPreviewProps) => {
  const isPublicView = useIsPublicView();

  const { data: groupData } = useQuery(GROUP_DETAIL_PREVIEW_QUERY, {
    variables: { id: groupId },
    skip: isPublicView,
  });
  const { data: publicGroupData } = useQuery(
    PUBLIC_GROUP_DETAIL_PREVIEW_QUERY,
    {
      variables: { id: groupId },
      skip: !isPublicView,
    },
  );

  const group = isPublicView ? publicGroupData?.group : groupData?.group;

  return group ? (
    <NestableLink
      className={styles.overlayLink}
      to={groupPath(group)}
      ignoreInsideLinks
    >
      <GroupDetailContent
        group={group}
        numLines={10}
        setMinHeight={setMinHeight}
      />
    </NestableLink>
  ) : null;
};

export default GroupDetailPreview;
